import React, { Component } from 'react';
import IRestClient from '../../restApi/IRestClient';
import AppUrl from '../../restApi/AppUrl';
import { motion } from 'framer-motion';

const BG = '#f8fafc';

const GLOBAL_CSS = `
/* ── Keyframes ──────────────────────────────────────── */
@keyframes soya-marquee-left {
  from { transform: translateX(0);    }
  to   { transform: translateX(-50%); }
}
@keyframes soya-marquee-right {
  from { transform: translateX(-50%); }
  to   { transform: translateX(0);    }
}
@keyframes soya-partners-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

/* ── Logo item wrapper ──────────────────────────────── */
.soya-logo-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
}

/* ── Logo image ─────────────────────────────────────── */
.soya-logo-item img {
  height: 72px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  display: block;
  filter: grayscale(0%) opacity(1);
  transition: filter 0.3s ease, transform 0.3s ease;
  pointer-events: none;
  user-select: none;
}

/* ── Desktop: full color always, scale on hover ───────── */
@media (hover: hover) {
  .soya-logo-item:hover img {
    transform: scale(1.1);
  }
  /* Pause both rows when hovering the zone */
  .soya-partners-zone:hover .soya-track-fwd,
  .soya-partners-zone:hover .soya-track-rev {
    animation-play-state: paused;
  }
}

/* ── Tablet ──────────────────────────────────────────── */
@media (min-width: 480px) {
  .soya-logo-item { padding: 0 24px; }
  .soya-logo-item img { height: 80px; max-width: 180px; }
}

/* ── Desktop ─────────────────────────────────────────── */
@media (min-width: 768px) {
  .soya-logo-item { padding: 0 36px; }
  .soya-logo-item img { height: 64px; max-width: 180px; }
}

@media (min-width: 1200px) {
  .soya-logo-item { padding: 0 48px; }
  .soya-logo-item img { height: 70px; max-width: 210px; }
}
`;

const ensureEnough = (arr, min = 10) => {
  if (!arr.length) return [];
  let r = [...arr];
  while (r.length < min) r = [...r, ...arr];
  return r;
};

/* ── Single marquee row ─────────────────────────────── */
const MarqueeRow = ({ items, trackClass, animName, duration }) => {
  const base    = ensureEnough(items);
  const doubled = [...base, ...base];

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* fade-left */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: 'clamp(40px, 8vw, 120px)',
        background: `linear-gradient(to right, ${BG}, transparent)`,
        zIndex: 2, pointerEvents: 'none',
      }} />
      {/* fade-right */}
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: 'clamp(40px, 8vw, 120px)',
        background: `linear-gradient(to left, ${BG}, transparent)`,
        zIndex: 2, pointerEvents: 'none',
      }} />

      <div
        className={trackClass}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: 'max-content',
          padding: '6px 0',
          animation: `${animName} ${duration}s linear infinite`,
          willChange: 'transform',
        }}
      >
        {doubled.map((item, idx) => (
          <div key={idx} className="soya-logo-item">
            <img
              src={item.company_logo || item.company_img}
              alt={item.company_name || 'Partner'}
              draggable={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Thin divider between rows ──────────────────────── */
const RowDivider = () => (
  <div style={{
    margin: '0 auto',
    width: '88%',
    maxWidth: 860,
    height: 1,
    background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.09), transparent)',
  }} />
);

/* ── Loading skeleton ───────────────────────────────── */
const Skeleton = () => (
  <section style={{ background: BG, padding: 'clamp(40px, 7vw, 80px) 0' }}>
    <style>{GLOBAL_CSS}</style>
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 20px', textAlign: 'center', marginBottom: 40 }}>
      {[130, 260, 190].map((w, i) => (
        <div key={i} style={{
          width: `min(${w}px, 80%)`,
          height: i === 0 ? 24 : i === 1 ? 32 : 16,
          borderRadius: i === 0 ? 999 : 6,
          background: '#dde3ed',
          margin: '0 auto 12px',
          animation: `soya-partners-pulse 1.5s ease ${i * 0.15}s infinite`,
        }} />
      ))}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(20px, 5vw, 48px)', overflow: 'hidden', padding: '8px 20px' }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{
          flexShrink: 0,
          width: 'clamp(70px, 18vw, 140px)',
          height: 'clamp(30px, 7vw, 54px)',
          borderRadius: 6, background: '#dde3ed',
          animation: `soya-partners-pulse 1.5s ease ${i * 0.12}s infinite`,
        }} />
      ))}
    </div>
  </section>
);

/* ── Main component ─────────────────────────────────── */
class CompanyTrust extends Component {
  constructor(props) {
    super(props);
    this.state = { myData: [], loading: true, error: false };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.Company)
      .then(result => {
        if (result == null) this.setState({ error: true, loading: false });
        else this.setState({ myData: result, loading: false });
      })
      .catch(() => this.setState({ error: true, loading: false }));
  }

  render() {
    const { myData, loading } = this.state;
    if (loading) return <Skeleton />;

    const row2 = myData.length > 1 ? [...myData].reverse() : myData;

    return (
      <section style={{ background: BG, overflow: 'hidden', padding: 'clamp(40px, 7vw, 88px) 0' }}>
        <style>{GLOBAL_CSS}</style>

        {/* ── Header ── */}
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          padding: '0 clamp(16px, 5vw, 40px)',
          textAlign: 'center',
          marginBottom: 'clamp(32px, 5vw, 60px)',
        }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Trusted Partners
            </span>
            <h2 style={{
              fontSize: 'clamp(20px, 4.5vw, 42px)',
              fontWeight: 800,
              color: '#0f172a',
              margin: '0 0 10px',
              lineHeight: 1.18,
            }}>
              Organizations That Trust Us
            </h2>
            <p style={{
              color: '#64748b',
              fontSize: 'clamp(13px, 2vw, 16px)',
              maxWidth: 420,
              margin: '0 auto',
              lineHeight: 1.65,
            }}>
              Proudly partnering with leading organizations to create lasting impact.
            </p>
          </motion.div>
        </div>

        {/* ── Dual marquee rows ── */}
        <motion.div
          className="soya-partners-zone"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.75, delay: 0.15 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(12px, 2.5vw, 28px)',
          }}
        >
          <MarqueeRow
            items={myData}
            trackClass="soya-track-fwd"
            animName="soya-marquee-left"
            duration={28}
          />

          <RowDivider />

          <MarqueeRow
            items={row2}
            trackClass="soya-track-rev"
            animName="soya-marquee-right"
            duration={36}
          />
        </motion.div>
      </section>
    );
  }
}

export default CompanyTrust;
